import { useLayoutEffect, useRef, type ReactElement, type ReactNode } from 'react';

export interface EditorShellProps {
  /** Accessible name of the toolbar row, e.g. "Report". */
  toolbarLabel: string;
  toolbar: ReactNode;
  /** Left pane (the widget palette); omitted in a view-only mode. */
  left?: ReactNode;
  /** Right pane (inspector, history, later explain); omitted to give the centre the width. */
  right?: ReactNode;
  children: ReactNode;
}

/** Literal names: Tailwind only emits utilities it finds written out in source. */
const EDITOR_COLS = {
  lcr: 'editor-cols-lcr',
  lc: 'editor-cols-lc',
  cr: 'editor-cols-cr',
  c: 'editor-cols-c'
} as const;

const SHELL_TOPBAR = 'var(--tfw-shell-topbar-height, 52px)';

/** Publishes the shell top bar plus this toolbar row as `--tfw-topbar-height`; the only writer while mounted. */
function useToolbarHeight(toolbarRef: React.RefObject<HTMLDivElement | null>): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const toolbar = toolbarRef.current;
    if (!toolbar) return;
    const publish = (height: number): void => {
      root.style.setProperty('--tfw-topbar-height', `calc(${SHELL_TOPBAR} + ${height}px)`);
    };
    publish(toolbar.getBoundingClientRect().height);
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((entries) => {
            const entry = entries[entries.length - 1];
            publish(entry?.borderBoxSize?.[0]?.blockSize ?? toolbar.getBoundingClientRect().height);
          });
    observer?.observe(toolbar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--tfw-topbar-height');
    };
  }, [toolbarRef]);
}

/** Toolbar row sticky under the top bar, then left / centre / right. The only writer of --tfw-topbar-height while mounted. */
export function EditorShell({ toolbarLabel, toolbar, left, right, children }: EditorShellProps): ReactElement {
  const toolbarRef = useRef<HTMLDivElement>(null);
  useToolbarHeight(toolbarRef);
  const cols = EDITOR_COLS[`${left ? 'l' : ''}c${right ? 'r' : ''}`];
  return (
    <div className="flex flex-col flex-1 min-w-0">
      <div
        ref={toolbarRef}
        role="toolbar"
        aria-label={toolbarLabel}
        className="sticky top-shell-bar z-bar flex flex-wrap items-center gap-3 px-6 py-2 bg-surface border-0 border-b border-solid border-line font-ui"
      >
        {toolbar}
      </div>
      <div data-editor-body className={`grid items-start gap-3 p-3 flex-1 min-h-0 ${cols}`}>
        {left ? <aside className="editor-side flex flex-col gap-3">{left}</aside> : null}
        <div className="min-w-0 isolate">{children}</div>
        {right ? <aside className="editor-side flex flex-col gap-3">{right}</aside> : null}
      </div>
    </div>
  );
}
